<template>
    <section class="summary-section">
        <div class="summary-container">
            <div class="container-header">
                <h3 id="order-summary-heading" class="headline-s">Order Summary</h3>
                <p class="text-rothcoBlack quantity" aria-label="Number of items in order">{{ props.currentList.total_quantity }} Items</p>
            </div>
            <dl>
                <dt class="paragraph-default" aria-label="Subtotal amount">Subtotal</dt>
                <dd class="paragraph-default">{{ summaryContent.formattedPrice }}</dd>
            </dl>
            <dl v-if="parseFloat(couponInformation?.discountAmount ?? 0) > 0">
                <dt class="paragraph-default" aria-label="Discount amount">Discount</dt>
                <dd class="paragraph-default text-rothcoRed font-italic">-${{ parseFloat(couponInformation?.discountAmount ?? 0)?.toFixed(2) }}</dd>
            </dl>
            <dl>
                <dt class="paragraph-default" aria-label="Shipping cost">Shipping</dt>
                <dd class="paragraph-default" v-if="props.selectedShippingMethod">{{ summaryContent.formattedShipping }}</dd>
            </dl>
            <dl>
                <dt class="paragraph-default" aria-label="Taxes">Taxes</dt>
                <dd v-if="hasShippingAddress" class="paragraph-default">{{ summaryContent.formattedTax }}</dd>
                <dd v-else class="paragraph-default font-italic">Enter Shipping Address</dd>
            </dl>
            <dl class="order-total">
                <dt class="title-l" aria-label="Total cost">Total</dt>
                <dd class="title-l">{{ summaryContent.formattedTotal }}</dd>
            </dl>
            <div class="bnpl-section">
                <div class="section-row" v-if="false">
                    <p class="paragraph-details text-rothcoBlack">From $97.79/mo with BNPL</p>
                    <a href="#" aria-label="Learn more about BNPL">Learn More</a>
                </div>
                <div class="section-row" v-if="!loggedIn">
                    <a @click.prevent="emit('openLoginTab')" class="paragraph-details"  href="#" aria-label="Sign in to your account">Sign in</a>
                    <span class="paragraph-details"> or </span>
                    <a href="#" @click.prevent="emit('createAccountClicked')" class="paragraph-details" aria-label="Create a new account">Create an Account</a>
                    <span class="paragraph-details"> to claim your tax exempt status</span>
                </div>

                <div v-if="couponInformation?.message" :class="couponCodeValid ? `notification notification-success` : `notification notification-error`">
                    <p class="paragraph-details">{{ couponInformation.message }}</p>
                </div>
                <form @submit.prevent class="discount-code-form input-folder" aria-label="Discount code form" v-if="!props.readonly" style="display: flex; flex-direction: row;">
                    <div class="input-folder--input">
                        <InputText 
                            id="gift_card_code" v-model="couponCodeInputValue"
                            :class="{
                                'input-field': true,
                                'invalid-state-input': couponCode && !couponCodeValid,
                            }"
                            fluid
                            placeholder=" "
                        />
                        <label
                            for="gift_card_code">
                            <p>Discount Code</p>
                        </label>
                        <div class="input-folder"><p class="input-support-text invalid-state" v-if="couponCode && !couponCodeValid">Code is invalid or does not apply to your order.</p></div>										
                        <div class="input-folder"><p class="input-support-text invalid" v-if="couponCode && couponCodeValid">Code successfully applied to your order!</p></div>
                    </div>
                    <button type="button" :class="couponCodeInputValue != null && couponCodeInputValue != '' ? `primary` : `bg-rothcoMedGray text-rothcoCharcoal`" aria-label="Apply discount code" @click="applyingCoupon()">
                        Apply
                    </button>
                </form>
            </div>
        </div>
        <div class="order-products">
            <h3 class="title-l">Items in Order</h3>
            <OrderItemsD2CCheckout 
                :current-list="props.currentList"
                v-model:list-items="computedListItems"
                :couponItems="props.couponItems"
                :restricted-items="props.restrictedItems"
                :readonly="props.readonly"
            />
        </div>
        <div class="order-footer-links-desktop">
            <a :href="helpRoute" target="_blank" aria-label="Get help">Need Help?</a>
            <a :href="returnPolicyRoute" target="_blank" aria-label="View Return Policy">Return Policy</a>
            <a :href="privacyPolicyRoute" target="_blank" aria-label="View Privacy Policy">Privacy Policy</a>
            <a :href="termsAndConditionsRoute" target="_blank" aria-label="View terms of Service">Terms of Service</a>
        </div>
    </section>
</template>
<script lang="ts" setup>
    import { ref, computed, defineEmits, watch, onMounted, onUnmounted, inject, toRefs} from "vue";
    import OrderItemsD2CCheckout from "../components/OrderItemsD2CCheckout.vue";
    import InputText from "primevue/inputtext";
    import { useFetch } from "@/helpers/composables/useFetch";
    import { useCheckoutD2CStore } from "@/store/checkoutD2CStore";
    import { storeToRefs } from "pinia";
    import { router } from "@inertiajs/vue3";
    import { getMainLineCouponItem } from "@/helpers/composables/useCouponPseudoLine";
    const emit = defineEmits(['openLoginTab', 'createAccountClicked']);
    let route = inject('route');
    let taxAmount = defineModel("tax", {
        type: Number,
        default: 0
    });
    let couponCode = defineModel("couponCode", {
        type: String,
        default: ""
    });
    let formattedTotal = defineModel("formattedTotal", {
        type: String,
        default: "",
    });
    let props = defineProps({
        currentList: {
            type: Object,
            default() {
                return {};
            }
        },
        selectedShippingMethod: {
            type: Object,
            default() {
                return {};
            }
        },
        listItems: {
            type: Array<Object>,
            default: ()=> []
        },
        couponItems: { 
            type: Array<Object>,
            default: ()=> []
        },
        restrictedItems: {
            type: Array<Object>,
            default: ()=> []
        },
        readonly: {
            type: Boolean,
            default: false
        },
        loggedIn: {
            type: Boolean,
            default: false
        },
    });

    const { applyCouponAndRecalculate } = useCheckoutD2CStore();
    const { couponInformation, hasShippingAddress } = storeToRefs(useCheckoutD2CStore())

    let {listItems} = toRefs(props);
    const subtotal = computed(() => {
        const couponItemPrice = couponInformation?.value?.items
            ?.filter(item => item.item_added_by_coupon === true)
                .reduce((sum, item) => sum + ((+item.priceWithoutDiscount) || 0), 0) ?? 0;

        return (+props.currentList.total || 0) + couponItemPrice;
    });

    let summaryContent = computed(() => {

        let formattedPrice = `$${subtotal.value.toFixed(2)}`;

        let formattedShipping =
            props.selectedShippingMethod.cost > 0
                ? `$${parseFloat(props.selectedShippingMethod.cost).toFixed(2)}`
                : "FREE";
        const total = +props.selectedShippingMethod.cost + +subtotal.value + +taxAmount.value - +(couponInformation?.value?.discountAmount ?? 0);
        let formattedTax = `$${parseFloat(taxAmount.value.toString()).toFixed(2)}`;
        let formattedTotal = `$${parseFloat((+total && +total > 0 ? total : 0).toString()).toFixed(2)}`
        return { formattedPrice, formattedShipping, formattedTax, formattedTotal };
    });

    const computedListItems = computed(()=>{
        return listItems.value.map((item)=>{
            let couponItem = getMainLineCouponItem(
                couponInformation.value?.items,
                item.item_variation_id
            );
            if (!couponItem) {
                    return item;
                }
            return {
                ...item,
                price: couponCodeValid.value && couponInformation.value && couponItem?.discountPrice !== null ? couponItem?.discountPrice : item.price,
                quantity: couponCodeValid && couponInformation ? couponItem?.quantity || item.quantity : item.quantity 
            }
        })
    })

    let couponCodeInputValue = ref("");
    let couponCodeValid = computed(() => {
        return couponInformation.value?.applied;
    });

    const applyingCoupon = ()=>{
        applyCouponAndRecalculate(couponCodeInputValue.value);
    }

    watch(()=>summaryContent.value, (newVal)=>{
        formattedTotal.value = newVal.formattedTotal;
    });
    let finishedEvent;
    onMounted(()=>{
        formattedTotal.value = `$${parseFloat((+props.selectedShippingMethod.cost + +subtotal.value).toString()).toFixed(2)}`;
        if(couponCode.value){
            couponCodeInputValue.value = couponCode.value;
            applyingCoupon();
        }

        finishedEvent =router.on('finish', (errors) => {
            if(couponCodeInputValue.value){
                applyingCoupon();
            }
        });
        
    });

    onUnmounted(()=>{
        if(finishedEvent){
            finishedEvent();
        }
    })
    let registerRoute = route('web.register');
    let termsAndConditionsRoute = computed(()=>route('index.cms', 'rothco-terms-conditions'));
    let privacyPolicyRoute = computed(()=>route('index.cms', 'rothco-privacy-policy'));
    let returnPolicyRoute = computed(()=>route('customer-service.cms', 'consumer-return-policy'));
    let helpRoute = computed(()=>route('customer-service.index'));
</script>
